<template>
  <div class="main-wrapper">
    <layoutheader></layoutheader>
    <breadcrumb-doctor title="My medicines" text="Doctor" text1="Medicines" />
    <div class="content">
      <div class="container">
        <div class="row">
          <doctorsidebar></doctorsidebar>
          <div class="col-md-7 col-lg-8 col-xl-9 adm-scope med">

            <div class="med-intro">
              <div>
                <h2 class="med-title">Your medicine list</h2>
                <p class="adm-muted med-sub">Medicines here show up first when you write a prescription, with their usual dose filled in.</p>
              </div>
              <div class="med-actions">
                <button type="button" class="adm-btn" @click="openForm()"><i class="fas fa-pen"></i> New medicine</button>
                <button type="button" class="adm-btn adm-btn--primary" @click="openCatalog"><i class="fas fa-plus"></i> Add from catalogue</button>
              </div>
            </div>

            <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

            <div v-if="mine.length" class="med-toolbar">
              <div class="adm-search med-search">
                <i class="fas fa-search"></i>
                <input v-model="q" class="adm-input" placeholder="Search name, brand or category" aria-label="Search my medicines" />
              </div>
              <div class="med-types" role="group" aria-label="Filter by type">
                <button type="button" class="med-type" :class="{ 'is-on': !type }" @click="type = ''">All <span>{{ mine.length }}</span></button>
                <button v-for="t in myTypes" :key="t.name" type="button" class="med-type" :class="{ 'is-on': type === t.name }" @click="type = type === t.name ? '' : t.name">
                  {{ t.name }} <span>{{ t.count }}</span>
                </button>
              </div>
            </div>

            <div v-if="loading" class="adm-card med-list">
              <div v-for="n in 5" :key="n" class="med-row"><div class="adm-skeleton" style="width: 45%"></div></div>
            </div>

            <div v-else-if="!mine.length" class="adm-card adm-empty med-empty">
              <i class="fas fa-pills"></i>
              <strong>Your medicine list is empty</strong>
              Add the medicines you prescribe often. Pick them from the catalogue or type in your own.
              <div class="med-empty__actions">
                <button type="button" class="adm-btn adm-btn--primary" @click="openCatalog"><i class="fas fa-plus"></i> Add from catalogue</button>
                <button type="button" class="adm-btn" @click="openForm()">New medicine</button>
              </div>
            </div>

            <div v-else-if="!shown.length" class="adm-card adm-empty">
              <i class="fas fa-search"></i>
              <strong>No medicine matches</strong>
              <button type="button" class="adm-btn adm-btn--sm" @click="q = ''; type = ''">Clear filters</button>
            </div>

            <div v-else class="adm-card med-list">
              <div v-for="m in shown" :key="m.medid" class="med-row">
                <span class="med-icon" :class="typeTone(m.medicinetype)" aria-hidden="true"><i :class="typeIcon(m.medicinetype)"></i></span>
                <div class="med-main">
                  <div class="med-name">
                    {{ m.medicinename }}
                    <span v-if="m.medstrength" class="med-strength">{{ m.medstrength }}</span>
                  </div>
                  <div class="med-meta">
                    <span>{{ m.medicinetype }}</span>
                    <span v-if="brand(m)">{{ brand(m) }}</span>
                    <span v-if="m.category">{{ m.category.trim() }}</span>
                  </div>
                  <div v-if="m.usagedetails" class="med-usage" :title="m.usagedetails">{{ m.usagedetails }}</div>
                </div>
                <button type="button" class="med-dose" :class="{ 'is-empty': !doseLabel(m.defaultdose) }" title="Usual dose" @click="openForm(m)">
                  <i class="far fa-clock"></i>
                  {{ doseLabel(m.defaultdose) || "Set usual dose" }}
                </button>
                <div class="med-row__actions">
                  <button type="button" class="adm-icon-btn" :aria-label="`Edit ${m.medicinename}`" title="Edit" @click="openForm(m)"><i class="fas fa-pen"></i></button>
                  <button type="button" class="adm-icon-btn med-del" :aria-label="`Remove ${m.medicinename}`" title="Remove" @click="removing = m"><i class="fas fa-trash-alt"></i></button>
                </div>
              </div>
            </div>

            <!-- Catalogue: tick several, add in one go -->
            <admin-modal v-if="catalogOpen" title="Add from catalogue" :subtitle="`${catalog.length} medicines · already added ones are marked`" size="lg" @close="catalogOpen = false">
              <div class="med-toolbar med-toolbar--modal">
                <div class="adm-search med-search">
                  <i class="fas fa-search"></i>
                  <input ref="catalogSearch" v-model="cq" class="adm-input" placeholder="Search the catalogue" aria-label="Search the catalogue" />
                </div>
                <select v-model="ctype" class="adm-select med-select" aria-label="Type">
                  <option value="">All types</option>
                  <option v-for="t in catalogTypes" :key="t" :value="t">{{ t }}</option>
                </select>
              </div>
              <div v-if="catalogLoading" class="med-cat"><div v-for="n in 6" :key="n" class="med-cat__item"><div class="adm-skeleton" style="width: 60%"></div></div></div>
              <div v-else-if="!catalogShown.length" class="adm-empty">
                <i class="fas fa-search"></i>
                <strong>Not in the catalogue</strong>
                <button type="button" class="adm-btn adm-btn--sm" @click="catalogOpen = false; openForm({ medicinename: cq.trim() }, true)">Create "{{ cq.trim() }}" yourself</button>
              </div>
              <div v-else class="med-cat">
                <label v-for="c in catalogShown" :key="c.medid" class="med-cat__item" :class="{ 'is-added': c.added, 'is-picked': picked[c.medid] }">
                  <input type="checkbox" :checked="c.added || !!picked[c.medid]" :disabled="c.added" @change="toggle(c)" />
                  <div class="med-main">
                    <div class="med-name">{{ c.medicinename }} <span v-if="c.medstrength" class="med-strength">{{ c.medstrength }}</span></div>
                    <div class="med-meta"><span>{{ c.medicinetype }}</span><span v-if="c.category">{{ c.category.trim() }}</span></div>
                  </div>
                  <span v-if="c.added" class="adm-badge adm-badge--active">Added</span>
                </label>
              </div>
              <template #footer>
                <span class="adm-muted med-picked">{{ pickedCount ? `${pickedCount} selected` : "Tick the medicines you prescribe" }}</span>
                <button type="button" class="adm-btn" @click="catalogOpen = false">Cancel</button>
                <button type="button" class="adm-btn adm-btn--primary" :disabled="!pickedCount || saving" @click="addPicked">
                  <span v-if="saving" class="adm-spinner"></span> Add {{ pickedCount || "" }} to my list
                </button>
              </template>
            </admin-modal>

            <!-- Create / edit one medicine -->
            <admin-modal v-if="form" :title="form.medid ? 'Edit medicine' : 'New medicine'" subtitle="Only the name and type are required" @close="form = null">
              <form id="med-form" class="adm-grid adm-grid--2" @submit.prevent="saveForm">
                <div class="adm-field adm-span-all">
                  <label class="adm-label" for="med-name">Medicine name *</label>
                  <input id="med-name" ref="formName" v-model="form.medicinename" class="adm-input" maxlength="100" placeholder="e.g. Paracetamol" required />
                </div>
                <div class="adm-field">
                  <label class="adm-label" for="med-type">Type *</label>
                  <select id="med-type" v-model="form.medicinetype" class="adm-select">
                    <option v-for="t in TYPES" :key="t" :value="t">{{ t }}</option>
                  </select>
                </div>
                <div class="adm-field">
                  <label class="adm-label" for="med-strength">Strength</label>
                  <input id="med-strength" v-model="form.medstrength" class="adm-input" maxlength="25" placeholder="e.g. 500 mg" />
                </div>
                <div class="adm-field">
                  <label class="adm-label" for="med-brand">Brand</label>
                  <input id="med-brand" v-model="form.brandname" class="adm-input" maxlength="100" placeholder="Optional" />
                </div>
                <div class="adm-field">
                  <label class="adm-label" for="med-cat">Category</label>
                  <input id="med-cat" v-model="form.category" class="adm-input" maxlength="75" list="med-categories" placeholder="e.g. Analgesic" />
                  <datalist id="med-categories"><option v-for="c in categories" :key="c" :value="c" /></datalist>
                </div>

                <fieldset class="adm-span-all med-dose-edit">
                  <legend class="adm-label">Usual dose <span class="adm-muted">(pre-filled on prescriptions)</span></legend>
                  <div class="med-dose-line">
                    <span class="med-dose-key">When</span>
                    <button v-for="(s, i) in SLOTS" :key="s" type="button" class="med-pill" :class="{ 'is-on': !form.customFreq && slotOn(i) }" @click="toggleSlot(i)">{{ s }}</button>
                    <button type="button" class="med-pill" :class="{ 'is-on': form.customFreq }" @click="toggleOther">Other</button>
                    <input v-if="form.customFreq" v-model="form.freq" class="adm-input med-freq" maxlength="20" placeholder="e.g. SOS, once a week" aria-label="Frequency" />
                  </div>
                  <div class="med-dose-line">
                    <span class="med-dose-key">Days</span>
                    <button v-for="d in DAYS" :key="d" type="button" class="med-pill" :class="{ 'is-on': String(form.days) === String(d) }" @click="form.days = String(form.days) === String(d) ? '' : String(d)">{{ d }}</button>
                    <input v-model="form.days" class="adm-input med-days" inputmode="numeric" maxlength="3" placeholder="—" aria-label="Number of days" @input="onDays" />
                  </div>
                  <p class="adm-muted med-dose-preview">{{ doseLabel(doseValue) ? `Shows as: ${doseLabel(doseValue)}` : "No usual dose — you'll fill it in each time." }}</p>
                </fieldset>

                <div class="adm-field adm-span-all">
                  <label class="adm-label" for="med-usage">Notes / usage</label>
                  <textarea id="med-usage" v-model="form.usagedetails" class="adm-textarea" rows="2" maxlength="500" placeholder="e.g. After food"></textarea>
                </div>
              </form>
              <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>
              <template #footer>
                <button type="button" class="adm-btn" @click="form = null">Cancel</button>
                <button type="submit" form="med-form" class="adm-btn adm-btn--primary" :disabled="saving || !form.medicinename.trim()">
                  <span v-if="saving" class="adm-spinner"></span> {{ form.medid ? "Save changes" : "Add medicine" }}
                </button>
              </template>
            </admin-modal>

            <admin-modal v-if="removing" title="Remove medicine?" size="sm" @close="removing = null">
              <p class="med-confirm"><strong>{{ removing.medicinename }}</strong> will be removed from your list. Past prescriptions are not affected.</p>
              <template #footer>
                <button type="button" class="adm-btn" @click="removing = null">Keep</button>
                <button type="button" class="adm-btn adm-btn--danger" :disabled="saving" @click="remove">
                  <span v-if="saving" class="adm-spinner"></span> Remove
                </button>
              </template>
            </admin-modal>

            <div class="adm-toasts" aria-live="polite">
              <div v-if="toast" class="adm-toast" :class="`adm-toast--${toast.kind}`">
                <i class="adm-toast__icon fas" :class="toast.kind === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'"></i>
                <div>{{ toast.text }}</div>
                <button type="button" class="adm-toast__close" aria-label="Dismiss" @click="toast = null"><i class="fas fa-times"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <doctorfooter />
  </div>
</template>

<script>
import axios from "axios";
import "../admin/admin.css";
import AdminModal from "../admin/components/AdminModal.vue";
import { errorMessage } from "../admin/admin-utils";

const SLOTS = ["Morning", "Afternoon", "Night"];
const DAYS = [3, 5, 7, 10, 15, 30];
const TYPES = ["Tablets", "Capsules", "Syrup", "Injection", "Drops", "Ointment", "Cream", "Lotion", "Inhalation", "Powder", "Solution", "Suppository", "Others"];
const FREQ_RE = /^\d-\d-\d$/;

const ICONS = {
  Tablets: "fas fa-tablets", Capsules: "fas fa-capsules", Syrup: "fas fa-prescription-bottle", Solution: "fas fa-prescription-bottle",
  Injection: "fas fa-syringe", Drops: "fas fa-eye-dropper", Inhalation: "fas fa-wind",
  Ointment: "fas fa-pump-medical", Cream: "fas fa-pump-medical", Lotion: "fas fa-pump-medical",
};
const TONES = { Tablets: "adm-tone-blue", Capsules: "adm-tone-violet", Syrup: "adm-tone-amber", Solution: "adm-tone-amber", Injection: "adm-tone-rose", Drops: "adm-tone-teal", Inhalation: "adm-tone-teal" };

const norm = v => (v || "").replace(/\s+/g, " ").trim().toLowerCase();
const keyOf = m => `${norm(m.medicinename)}|${norm(m.medicinetype)}|${norm(m.medstrength)}`;
// "n/a" (and the odd "n\a") is the old placeholder for "no brand".
const cleanBrand = b => (b && !/^n[/\\]a$/i.test(b.trim()) ? b.trim() : "");

export default {
  name: "DoctorMedicine",
  components: { AdminModal },
  data() {
    return {
      SLOTS, DAYS, TYPES,
      mine: [],
      loading: true,
      error: "",
      q: "",
      type: "",
      catalog: [],
      catalogOpen: false,
      catalogLoading: false,
      cq: "",
      ctype: "",
      picked: {},
      form: null,
      formError: "",
      removing: null,
      saving: false,
      toast: null,
    };
  },
  computed: {
    doctorId() {
      return sessionStorage.getItem("userdbid");
    },
    myTypes() {
      const counts = {};
      this.mine.forEach(m => { counts[m.medicinetype] = (counts[m.medicinetype] || 0) + 1; });
      return Object.keys(counts).sort().map(name => ({ name, count: counts[name] }));
    },
    shown() {
      const q = norm(this.q);
      return this.mine.filter(m => (!this.type || m.medicinetype === this.type)
        && (!q || norm(`${m.medicinename} ${cleanBrand(m.brandname)} ${m.category} ${m.medstrength}`).includes(q)));
    },
    myKeys() {
      return new Set(this.mine.map(keyOf));
    },
    catalogTypes() {
      return [...new Set(this.catalog.map(c => c.medicinetype))].sort();
    },
    catalogShown() {
      const q = norm(this.cq);
      return this.catalog
        .filter(c => (!this.ctype || c.medicinetype === this.ctype) && (!q || norm(`${c.medicinename} ${cleanBrand(c.brandname)} ${c.category}`).includes(q)))
        .map(c => ({ ...c, added: this.myKeys.has(keyOf(c)) }));
    },
    pickedCount() {
      return Object.keys(this.picked).length;
    },
    categories() {
      return [...new Set([...this.mine, ...this.catalog].map(m => (m.category || "").trim()).filter(Boolean))].sort();
    },
    doseValue() {
      if (!this.form) return "";
      const freq = (this.form.freq || "").trim();
      const days = (this.form.days || "").trim();
      return freq || days ? `${freq},${days}` : "";
    },
  },
  methods: {
    brand: m => cleanBrand(m.brandname),
    typeIcon: t => ICONS[t] || "fas fa-pills",
    typeTone: t => TONES[t] || "adm-tone-green",
    doseLabel(dose) {
      const [freq = "", days = ""] = (dose || "").split(",").map(s => s.trim());
      let when = freq;
      if (FREQ_RE.test(freq)) {
        when = freq.split("-").map((n, i) => (n !== "0" ? (n === "1" ? SLOTS[i] : `${SLOTS[i]} ×${n}`) : "")).filter(Boolean).join(" · ");
      }
      const d = days && `${days} day${days === "1" ? "" : "s"}`;
      return [when, d].filter(Boolean).join(" — ");
    },
    notify(text, kind = "success") {
      this.toast = { text, kind };
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toast = null; }, 3500);
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/medicines`);
        this.mine = data || [];
      } catch (error) {
        this.error = errorMessage(error, "Could not load your medicines.");
      } finally {
        this.loading = false;
      }
    },
    async openCatalog() {
      this.catalogOpen = true;
      this.picked = {};
      this.cq = "";
      this.ctype = "";
      this.$nextTick(() => this.$refs.catalogSearch && this.$refs.catalogSearch.focus());
      if (this.catalog.length) return;
      this.catalogLoading = true;
      try {
        const { data } = await axios.get("/doctors/medicines");
        this.catalog = data || [];
      } catch (error) {
        this.catalogOpen = false;
        this.notify(errorMessage(error, "Could not load the catalogue."), "error");
      } finally {
        this.catalogLoading = false;
      }
    },
    toggle(c) {
      if (this.picked[c.medid]) delete this.picked[c.medid];
      else this.picked[c.medid] = c;
    },
    payload(m) {
      return {
        medicinename: m.medicinename.trim(),
        brandname: (m.brandname || "").trim() || "n/a",
        category: (m.category || "").trim() || "Others",
        medicinetype: m.medicinetype || "Others",
        medstrength: (m.medstrength || "").trim() || null,
        usagedetails: (m.usagedetails || "").trim() || null,
        defaultdose: m.defaultdose || null,
      };
    },
    async addPicked() {
      this.saving = true;
      try {
        const items = Object.values(this.picked).map(c => this.payload(c));
        const { data } = await axios.post(`/doctors/${this.doctorId}/medicines`, items);
        this.mine = [...this.mine, ...data].sort((a, b) => norm(a.medicinename).localeCompare(norm(b.medicinename)));
        this.catalogOpen = false;
        this.notify(`${data.length} medicine${data.length === 1 ? "" : "s"} added. Set a usual dose to save time on prescriptions.`);
      } catch (error) {
        this.notify(errorMessage(error, "Could not add the medicines."), "error");
      } finally {
        this.saving = false;
      }
    },
    openForm(m = null, isNew = false) {
      const src = m || {};
      const [freq = "", days = ""] = (src.defaultdose || "").split(",").map(s => s.trim());
      this.form = {
        medid: isNew ? null : src.medid || null,
        medicinename: src.medicinename || "",
        medicinetype: src.medicinetype || "Tablets",
        medstrength: src.medstrength || "",
        brandname: cleanBrand(src.brandname),
        category: (src.category || "").trim(),
        usagedetails: src.usagedetails || "",
        freq,
        customFreq: !!freq && !FREQ_RE.test(freq),
        days: src.medid ? days : days || "5",
      };
      this.formError = "";
      this.$nextTick(() => this.$refs.formName && this.$refs.formName.focus());
    },
    slotOn(i) {
      return FREQ_RE.test(this.form.freq) && this.form.freq.split("-")[i] !== "0";
    },
    toggleSlot(i) {
      const parts = FREQ_RE.test(this.form.freq) ? this.form.freq.split("-") : ["0", "0", "0"];
      parts[i] = parts[i] === "0" ? "1" : "0";
      this.form.freq = parts.join("-") === "0-0-0" ? "" : parts.join("-");
      this.form.customFreq = false;
    },
    toggleOther() {
      this.form.customFreq = !this.form.customFreq;
      // Switching either way starts clean rather than carrying the other kind over.
      if (this.form.customFreq === FREQ_RE.test(this.form.freq)) this.form.freq = "";
    },
    onDays() {
      this.form.days = this.form.days.replace(/\D/g, "");
    },
    async saveForm() {
      const f = this.form;
      if (!f.medicinename.trim()) return;
      const body = this.payload({ ...f, defaultdose: this.doseValue });
      if (!f.medid && this.myKeys.has(keyOf(body))) {
        this.formError = "This medicine is already in your list.";
        return;
      }
      this.saving = true;
      this.formError = "";
      try {
        if (f.medid) {
          await axios.put(`/doctors/${this.doctorId}/medicines/${f.medid}`, body);
          const i = this.mine.findIndex(m => m.medid === f.medid);
          if (i >= 0) this.mine.splice(i, 1, { ...this.mine[i], ...body });
          this.notify(`${body.medicinename} updated.`);
        } else {
          const { data } = await axios.post(`/doctors/${this.doctorId}/medicines`, [body]);
          if (!data.length) {
            this.formError = "This medicine is already in your list.";
            return;
          }
          this.mine = [...this.mine, ...data].sort((a, b) => norm(a.medicinename).localeCompare(norm(b.medicinename)));
          this.notify(`${body.medicinename} added.`);
        }
        this.form = null;
      } catch (error) {
        this.formError = errorMessage(error, "Could not save the medicine.");
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      const m = this.removing;
      this.saving = true;
      try {
        await axios.delete(`/doctors/${this.doctorId}/medicines/${m.medid}`);
        this.mine = this.mine.filter(x => x.medid !== m.medid);
        this.notify(`${m.medicinename} removed.`);
        this.removing = null;
      } catch (error) {
        this.notify(errorMessage(error, "Could not remove the medicine."), "error");
      } finally {
        this.saving = false;
      }
    },
  },
  created() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.toastTimer);
  },
};
</script>

<style scoped>
.med {
  font-size: 14px;
}

.med-intro {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.med-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 2px;
  color: var(--adm-ink);
}

.med-sub {
  margin: 0;
  font-size: 13px;
}

.med-actions,
.med-empty__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.med-empty__actions {
  justify-content: center;
  margin-top: 12px;
}

.med-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.med-toolbar--modal {
  margin-bottom: 10px;
}

.med-search {
  flex: 1 1 240px;
  max-width: none;
}

.med-select {
  width: auto;
  min-width: 150px;
}

.med-types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.med-type,
.med-pill {
  border: 1px solid var(--adm-line);
  background: #fff;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--adm-ink-2);
  cursor: pointer;
  line-height: 1.3;
}

.med-type span {
  color: var(--adm-muted);
  font-weight: 500;
  margin-left: 2px;
}

.med-type.is-on,
.med-pill.is-on {
  background: var(--adm-primary);
  border-color: var(--adm-primary);
  color: #fff;
}

.med-type.is-on span {
  color: rgba(255, 255, 255, 0.8);
}

.med-list {
  padding: 0;
  overflow: hidden;
}

.med-row {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--adm-line);
}

.med-row:last-child {
  border-bottom: 0;
}

.med-row:hover {
  background: var(--adm-surface);
}

.med-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-size: 16px;
}

.med-main {
  min-width: 0;
}

.med-name {
  font-weight: 600;
  color: var(--adm-ink);
}

.med-strength {
  font-weight: 500;
  color: var(--adm-muted);
  font-size: 13px;
  margin-left: 4px;
}

.med-meta {
  font-size: 12.5px;
  color: var(--adm-muted);
}

.med-meta span + span::before {
  content: "·";
  margin: 0 6px;
}

.med-usage {
  font-size: 12.5px;
  color: var(--adm-ink-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.med-dose {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--adm-line);
  background: var(--adm-surface);
  color: var(--adm-ink-2);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.med-dose.is-empty {
  border-style: dashed;
  background: #fff;
  color: var(--adm-muted);
  font-weight: 500;
}

.med-row__actions {
  display: flex;
  gap: 4px;
}

.med-del:hover {
  color: #d92d20;
}

.med-cat {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 8px;
  max-height: 52vh;
  overflow-y: auto;
  padding: 2px;
}

.med-cat__item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--adm-line);
  border-radius: 10px;
  padding: 9px 12px;
  margin: 0;
  cursor: pointer;
  font-weight: 400;
}

.med-cat__item input {
  width: 16px;
  height: 16px;
  flex: none;
  accent-color: var(--adm-primary);
}

.med-cat__item .med-main {
  flex: 1;
}

.med-cat__item.is-picked {
  border-color: var(--adm-primary);
  box-shadow: 0 0 0 1px var(--adm-primary);
}

.med-cat__item.is-added {
  cursor: default;
  background: var(--adm-surface);
  opacity: 0.75;
}

.med-picked {
  margin-right: auto;
  font-size: 13px;
}

.med-dose-edit {
  border: 1px solid var(--adm-line);
  border-radius: 10px;
  padding: 10px 14px 6px;
  margin: 0;
}

.med-dose-edit legend {
  width: auto;
  padding: 0 6px;
  margin: 0;
  font-size: 13px;
}

.med-dose-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.med-dose-key {
  width: 48px;
  font-size: 12.5px;
  color: var(--adm-muted);
  font-weight: 600;
}

.med-freq {
  width: 180px;
  padding: 5px 10px;
}

.med-days {
  width: 64px;
  padding: 5px 10px;
  text-align: center;
}

.med-dose-preview {
  font-size: 12.5px;
  margin: 0 0 4px;
}

.med-confirm {
  margin: 0;
}

@media (max-width: 767px) {
  .med-row {
    grid-template-columns: 36px minmax(0, 1fr) auto;
  }

  .med-dose {
    grid-column: 2 / -1;
    justify-self: start;
  }

  .med-row__actions {
    grid-row: 1;
    grid-column: 3;
  }
}
</style>
